import {createStyles} from '@mantine/core';
import React, {useCallback, useRef, useState} from 'react';

import Float from 'ui-library/floating-ui/Float';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import useStableValue from 'ui-library/hooks/useStableValue';
import OptionContent from 'ui-library/menus/OptionContent';

import {GetTableDataFn} from './extractTableDataForSuggestions';
import SuggestedPlotsPanel from './SuggestedPlotsPanel';
import {prefetchSuggestPlots} from './suggestPlotsState';

const PANEL_WIDTH = 350;

interface SuggestedPlotsHoverOptionProps {
  entryId: string;
  tableName: string;
  getTableData: GetTableDataFn;
  onSuggestionChosen?: () => void;
}

export default function SuggestedPlotsHoverOption({
  entryId,
  tableName,
  getTableData,
  onSuggestionChosen,
}: SuggestedPlotsHoverOptionProps): JSX.Element {
  const {classes} = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const [dropLeft, setDropLeft] = useState(false);
  const getTableDataFn = useStableValue<GetTableDataFn>(getTableData);

  const handleMouseEnter = useCallback(() => {
    prefetchSuggestPlots({entryId, tableName, getTableData: getTableDataFn});
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropLeft(window.innerWidth - rect.right < PANEL_WIDTH);
    }
  }, [entryId, tableName, getTableDataFn]);

  return (
    <Float
      interaction={FloatingInteraction.HOVER}
      placement={dropLeft ? FloatingPlacement.LEFT_START : FloatingPlacement.RIGHT_START}
      delay={{open: 200, close: 400}}
      restMs={40}
      stealFocus={false}
      role="dialog"
      floatingElementOptions={{
        floatingBodyClassName: classes.floatingBody,
      }}
      renderContent={() => (
        <SuggestedPlotsPanel
          entryId={entryId}
          tableName={tableName}
          getTableData={getTableData}
          onClose={onSuggestionChosen}
          autoFetch={true}
        />
      )}
    >
      <div ref={containerRef} onMouseEnter={handleMouseEnter} className={classes.optionWrapper}>
        <OptionContent hasSubmenu={true}>AI Suggested Plots</OptionContent>
      </div>
    </Float>
  );
}

const useStyles = createStyles((theme) => ({
  optionWrapper: {
    width: '100%',
  },
  floatingBody: {
    backgroundColor: theme.white,
    borderRadius: theme.radius.md,
    boxShadow: theme.shadows.md,
  },
}));
